import { useCallback, useEffect, useState } from 'react';
import { Button, Select, Typography, message } from 'antd';
import { AppstoreOutlined, TeamOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import ParamListView from '../../../components/ParamListView';
import {
  getAllUsers,
  getAvailableCollections,
  getModules,
  getUserId,
} from '../../../services/adminApi';
import { fetchJsonWithAuth } from '../../../services/authApi';
import { AUTH_URL } from '../../../services/apiConfig';
import '../../../styles/UserSettingsSection.css';

const { Text } = Typography;

const DEFAULT_MODULE_TABS = [
  { key: 'job',         label: 'Jobs'        },
  { key: 'candidate',   label: 'Candidates'  },
  { key: 'submissions', label: 'Submissions' },
];

function getUserDisplayName(raw) {
  return (
    raw?.USER_NAME ?? raw?.user_name ?? raw?.username ??
    raw?.name      ?? raw?.full_name ?? raw?.fullName ??
    raw?.email     ?? `User ${raw?.USER_ID ?? raw?.user_id ?? '?'}`
  );
}

function normalizeUserRecord(raw) {
  return {
    ...raw,
    key:      raw.USER_ID   ?? raw.user_id   ?? raw.userId ?? raw.id ?? raw._id,
    _name:    getUserDisplayName(raw),
    _email:   raw.email     ?? raw.EMAIL     ?? raw.user_email ?? '',
    _role:    raw.role_name ?? raw.roleName  ?? raw.ROLE_NAME  ?? '',
    _rawRef:  raw,
  };
}

const API_MODULE_MAP = { job: 'jobs', candidate: 'candidates', submissions: 'submissions' };

function getUserRoleIdForSeed(user) {
  const value = user?.raw?.ROLE_ID ?? user?.raw?.role_id ?? user?.raw?.roleId ?? user?.raw?.ROLEID
    ?? user?.ROLE_ID ?? user?.role_id ?? user?.roleId ?? user?.ROLEID ?? 0;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

function firstFieldConfigArray(json) {
  const payload = json?.data ?? json;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.visibleFields)) return payload.visibleFields;
  if (Array.isArray(json?.visibleFields)) return json.visibleFields;
  return [];
}

async function hasFieldConfigDataForSeed(user, configType, modules) {
  const userId = user ? getUserId(user) : 0;
  const roleId = user ? getUserRoleIdForSeed(user) : 0;
  const results = await Promise.all(
    modules.map(async ({ key, apiModule }) => {
      const moduleName = apiModule ?? API_MODULE_MAP[key] ?? key;
      const params = new URLSearchParams({
        module: moduleName,
        userId: String(userId ?? ''),
        roleId: String(roleId ?? ''),
        configType,
      });
      try {
        const json = await fetchJsonWithAuth(AUTH_URL, `/admin/field-config?${params}`);
        return firstFieldConfigArray(json).length > 0;
      } catch {
        return false;
      }
    })
  );
  return results.some(Boolean);
}

function seedFieldConfigForCollections(collections) {
  return fetchJsonWithAuth(AUTH_URL, '/admin/field-config/seed', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collections }),
  });
}

function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function normalizeModuleTab(module) {
  if (typeof module === 'string') {
    return { key: module, apiModule: module, label: moduleTitle(module) };
  }

  const key = (
    module?.key ?? module?.moduleKey ?? module?.value ?? module?.module ??
    module?.apiModule ?? module?.name ?? module?.modulename ?? module?.menuName
  );
  if (!key) return null;

  const apiModule = (
    module?.apiModule ?? module?.api_module ?? module?.module ?? module?.value ??
    module?.key ?? module?.name ?? module?.modulename ?? module?.menuName ?? key
  );

  return {
    key,
    apiModule,
    label: (
      module?.label ?? module?.moduleLabel ?? module?.moduleName ??
      module?.name ?? module?.modulename ?? module?.menuName ?? moduleTitle(key)
    ),
  };
}

function normalizeModuleTabs(modules) {
  const source = Array.isArray(modules) && modules.length ? modules : DEFAULT_MODULE_TABS;
  const seen = new Set();
  const tabs = source
    .map(normalizeModuleTab)
    .filter(Boolean)
    .filter((module) => {
      if (seen.has(module.key)) return false;
      seen.add(module.key);
      return true;
    });

  return tabs.length ? tabs : DEFAULT_MODULE_TABS;
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function UsersSection() {
  const [users, setUsers]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [page, setPage]                 = useState(1);
  const [pageSize, setPageSize]         = useState(10);
  const [searchQuery, setSearchQuery]   = useState('');
  const [collections, setCollections]   = useState([]);
  const [seedCollections, setSeedCollections] = useState([]);
  const [hasSeedData, setHasSeedData] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadSeedState = useCallback(async () => {
    setSeedLoading(true);
    try {
      const [rawModules, colls] = await Promise.all([
        getModules().catch(() => []),
        getAvailableCollections(),
      ]);
      const tabs = normalizeModuleTabs(rawModules);
      const state = await Promise.all(['listView', 'filter', 'form'].map((type) => (
        hasFieldConfigDataForSeed(null, type, tabs)
      )));
      setCollections(colls);
      setHasSeedData(state.some(Boolean));
    } catch {
      message.error('Failed to load seed status');
    } finally {
      setSeedLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async ({ offset, limit, searchQuery: sq } = {}) => {
    setLoading(true);
    try {
      const result = await getAllUsers({ offset, limit, searchQuery: sq ?? '' });
      const normalized = (result.users ?? []).map(normalizeUserRecord);
      setUsers(normalized);
      setTotal(result.total ?? normalized.length);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => {
      fetchUsers({ offset: (page - 1) * pageSize, limit: pageSize, searchQuery });
    });
  }, [fetchUsers, page, pageSize, searchQuery]);

  useEffect(() => {
    queueMicrotask(() => {
      loadSeedState();
    });
  }, [loadSeedState]);

  async function handleSeedFieldConfig() {
    if (hasSeedData) {
      message.info('Field config already exists. Seed is not allowed.');
      return;
    }
    if (seedCollections.length === 0) {
      message.warning('Select at least one collection to seed.');
      return;
    }

    setSeeding(true);
    try {
      await seedFieldConfigForCollections(seedCollections);
      message.success('Field config seeded');
      setSeedCollections([]);
      await loadSeedState();
    } catch (error) {
      message.error(error?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  }

  const fields = [
    {
      label: 'Name', value: '_name', width: 220,
      render: (name, record) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{name}</Text>
          {record._email && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{record._email}</div>}
        </div>
      ),
    },
    { label: 'Role',  value: '_role',  width: 180 },
  ];

  return (
    <>
      <AppCard
        icon={<AppstoreOutlined />}
        title="Field Config Seed"
        style={{ marginBottom: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 320, flex: 1 }}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              value={seedCollections}
              options={collections.map((collection) => ({ label: collection, value: collection }))}
              placeholder={hasSeedData ? 'Field config already exists' : 'Select collections to seed'}
              disabled={hasSeedData || seedLoading || seeding}
              loading={seedLoading}
              onChange={setSeedCollections}
              style={{ width: '100%' }}
            />
          </div>
          <Button
            type="primary"
            loading={seeding}
            disabled={hasSeedData || seedLoading || seedCollections.length === 0}
            onClick={handleSeedFieldConfig}
          >
            Seed Field Config
          </Button>
        </div>
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          {hasSeedData
            ? 'Seed is disabled because field config data already exists.'
            : 'Select collections and seed default field configuration for this project.'}
        </Text>
      </AppCard>
      <AppCard icon={<TeamOutlined />} title="Users"
        style={{ marginBottom: 0 }}
        bodyStyle={{ padding: 0 }}>
        <ParamListView
          listName="Users"
          dataSource={users}
          fields={fields}
          loading={loading}
          total={total}
          current={page}
          pageSize={pageSize}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          onSearch={(q) => { setSearchQuery(q); setPage(1); }}
        />
      </AppCard>
    </>
  );
}
